/**
 * VisionAgent.js — Dedicated page for the Vision AI Agent (Text ↔ Image).
 *
 * Two modes:
 *   • Text → Image  (Stable Diffusion 3 Medium / FLUX via Replicate)
 *   • Image → Text  (Qwen2.5-VL-72B-Instruct via OVHcloud / HF Inference Providers)
 *
 * Theme: follows the existing marketplace orange/dark design system.
 */

import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ImagePlus, Wand2, ArrowLeft, Download, Copy, Sparkles,
  RefreshCw, Upload, X, Loader2, Eye, FileText, ChevronDown,
  Cpu, Zap, Star,
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

axios.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Model catalogue ──────────────────────────────────────────────────────────
const TEXT_TO_IMAGE_MODELS = [
  {
    id: 'stabilityai/stable-diffusion-3-medium',
    name: 'Stable Diffusion 3 Medium',
    badge: 'Recommended',
    badgeColor: '#F87217',
    description: 'Stability AI · via Replicate',
    icon: '🎨',
  },
  {
    id: 'black-forest-labs/FLUX.1-schnell',
    name: 'FLUX.1 Schnell',
    badge: 'Ultra-Fast',
    badgeColor: '#10b981',
    description: 'Black Forest Labs · via Replicate',
    icon: '⚡',
  },
  {
    id: 'black-forest-labs/FLUX.1-dev',
    name: 'FLUX.1 Dev',
    badge: 'High Quality',
    badgeColor: '#6366f1',
    description: 'Black Forest Labs · via Replicate',
    icon: '✨',
  },
];

// Image-to-text model catalogue. Keep this in lockstep with the backend's
// _IMAGE_TO_TEXT_MODELS list in vision_routes.py — only list models actually
// deployed on the "ovhcloud" HF Inference Provider, or the backend will
// silently fall back to its default and this selector will lie about what
// ran. Verify at https://huggingface.co/<model>?inference_provider=ovhcloud
// before adding entries here.
const IMAGE_TO_TEXT_MODELS = [
  {
    id: 'Qwen/Qwen2.5-VL-72B-Instruct',
    label: 'Qwen2.5-VL 72B',
    badge: '★ OVHcloud',
    color: '#F87217',
  },
];

// ── Sample prompts ───────────────────────────────────────────────────────────
const SAMPLE_PROMPTS = [
  'Astronaut riding a horse on the moon, photorealistic, cinematic lighting',
  'Enchanted forest at dusk with glowing fireflies and a hidden waterfall, fantasy art',
  'Futuristic Tokyo skyline at night, neon reflections on rain-soaked streets, cyberpunk',
  'Golden retriever puppy playing in autumn leaves, soft natural light, Canon 5D',
  'Abstract geometric art — fractals merging into a cosmic nebula, vibrant colors',
  'Cozy cabin interior by a roaring fireplace, snowfall outside the window, warm tones',
];

// ── Utility ──────────────────────────────────────────────────────────────────
function downloadBase64Image(b64, filename = 'vision-agent.png') {
  const link = document.createElement('a');
  link.href = `data:image/png;base64,${b64}`;
  link.download = filename;
  link.click();
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
}

// ── Sub-component: Mode Tab ──────────────────────────────────────────────────
function ModeTab({ active, onClick, icon: Icon, label, isDark }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 20px',
        borderRadius: 10,
        border: 'none',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: 14,
        transition: 'all 0.2s',
        background: active
          ? 'linear-gradient(135deg, #F87217, #ff9a4e)'
          : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
        color: active ? '#fff' : isDark ? '#94a3b8' : '#64748b',
        boxShadow: active ? '0 4px 16px rgba(248,114,23,0.35)' : 'none',
      }}
    >
      <Icon size={16} />
      {label}
    </button>
  );
}

// ── Sub-component: Model Selector ────────────────────────────────────────────
function ModelSelector({ selected, onChange, isDark }) {
  const [open, setOpen] = useState(false);
  const current = TEXT_TO_IMAGE_MODELS.find(m => m.id === selected) || TEXT_TO_IMAGE_MODELS[0];

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 16px',
          borderRadius: 10,
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          background: isDark ? '#0f1729' : '#fff',
          color: isDark ? '#f0f6fc' : '#0f172a',
          cursor: 'pointer',
          width: '100%',
          fontWeight: 500,
          fontSize: 14,
        }}
      >
        <span style={{ fontSize: 18 }}>{current.icon}</span>
        <span style={{ flex: 1, textAlign: 'left' }}>{current.name}</span>
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 700,
            background: current.badgeColor + '22',
            color: current.badgeColor,
          }}
        >
          {current.badge}
        </span>
        <ChevronDown size={16} style={{ opacity: 0.5 }} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 50,
            borderRadius: 12,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            background: isDark ? '#0f1729' : '#fff',
            boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
            overflow: 'hidden',
          }}
        >
          {TEXT_TO_IMAGE_MODELS.map(m => (
            <button
              key={m.id}
              onClick={() => { onChange(m.id); setOpen(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                width: '100%',
                border: 'none',
                background: selected === m.id
                  ? (isDark ? 'rgba(248,114,23,0.12)' : 'rgba(248,114,23,0.08)')
                  : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 20 }}>{m.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: isDark ? '#f0f6fc' : '#0f172a' }}>{m.name}</div>
                <div style={{ fontSize: 12, color: isDark ? '#64748b' : '#94a3b8' }}>{m.description}</div>
              </div>
              {selected === m.id && (
                <Star size={14} fill="#F87217" color="#F87217" />
              )}
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  background: m.badgeColor + '22',
                  color: m.badgeColor,
                }}
              >
                {m.badge}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sub-component: Generated Image Card ─────────────────────────────────────
function GeneratedImageCard({ item, isDark, onReuse }) {
  return (
    <div
      style={{
        borderRadius: 16,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
        background: isDark ? '#0f1729' : '#fff',
        overflow: 'hidden',
        boxShadow: isDark
          ? '0 4px 24px rgba(0,0,0,0.5)'
          : '0 4px 24px rgba(0,0,0,0.08)',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = isDark
          ? '0 12px 40px rgba(0,0,0,0.7)'
          : '0 12px 40px rgba(0,0,0,0.14)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = isDark
          ? '0 4px 24px rgba(0,0,0,0.5)'
          : '0 4px 24px rgba(0,0,0,0.08)';
      }}
    >
      {/* Image */}
      <div style={{ position: 'relative', aspectRatio: '1/1', overflow: 'hidden', background: '#111' }}>
        <img
          src={`data:image/png;base64,${item.image_b64}`}
          alt={item.prompt}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {/* Overlay actions */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)',
            display: 'flex',
            alignItems: 'flex-end',
            padding: 12,
            gap: 8,
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
          className="image-overlay"
          onMouseEnter={e => e.currentTarget.style.opacity = 1}
          onMouseLeave={e => e.currentTarget.style.opacity = 0}
        >
          <button
            onClick={() => downloadBase64Image(item.image_b64, `vision-${Date.now()}.png`)}
            title="Download"
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 8,
              padding: '6px 12px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              backdropFilter: 'blur(8px)',
            }}
          >
            <Download size={13} /> Download
          </button>
          <button
            onClick={() => onReuse(item.prompt)}
            title="Reuse prompt"
            style={{
              background: 'rgba(248,114,23,0.7)',
              border: '1px solid rgba(248,114,23,0.5)',
              borderRadius: 8,
              padding: '6px 12px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              backdropFilter: 'blur(8px)',
            }}
          >
            <RefreshCw size={13} /> Reuse
          </button>
        </div>
        {/* Show overlay on hover via JS since we can't use CSS :hover on inline-styles only */}
        <style>{`.image-overlay:hover { opacity: 1 !important; }`}</style>
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 14px' }}>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: isDark ? '#94a3b8' : '#475569',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.4',
          }}
        >
          {item.prompt}
        </p>
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, color: isDark ? '#475569' : '#94a3b8' }}>
            {item.width}×{item.height}
          </span>
          <span style={{ fontSize: 11, color: isDark ? '#475569' : '#94a3b8' }}>
            {item.model?.split('/').pop()}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Main Page Component ──────────────────────────────────────────────────────
export default function VisionAgent() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { token } = useAuth();

  // Mode
  const [mode, setMode] = useState('text-to-image'); // 'text-to-image' | 'image-to-text'

  // Text-to-image state
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState(TEXT_TO_IMAGE_MODELS[0].id);
  const [generating, setGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]); // gallery

  // Image-to-text state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [captioning, setCaptioning] = useState(false);
  const [caption, setCaption] = useState('');
  const [i2tModel, setI2tModel] = useState(IMAGE_TO_TEXT_MODELS[0].id);
  const [i2tPrompt, setI2tPrompt] = useState('Describe this image in detail.');
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // ── Text-to-image generate ────────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) { toast.error('Please enter a prompt'); return; }
    setGenerating(true);
    const toastId = toast.loading('Generating image\u2026 (may take 30-60s on first run)');
    try {
      const res = await axios.post(
        `${API}/vision/text-to-image`,
        {
          prompt: prompt.trim(),
          negative_prompt: negativePrompt.trim() || undefined,
          model: selectedModel,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 260_000, // 260s — Replicate cold-start can be slow
        },
      );
      setGeneratedImages(prev => [res.data, ...prev]);
      toast.success('Image generated!', { id: toastId });
    } catch (err) {
      const detail = err.response?.data?.detail || (err.code === 'ECONNABORTED' ? 'Request timed out \u2014 please retry.' : err.message);
      toast.error(`Generation failed: ${detail}`, { id: toastId });
    } finally {
      setGenerating(false);
    }
  }, [prompt, negativePrompt, selectedModel, token]);

  // ── Image-to-text caption ─────────────────────────────────────────────────
  const handleCaption = useCallback(async () => {
    if (!uploadedFile) { toast.error('Please upload an image'); return; }
    setCaptioning(true);
    setCaption('');
    const toastId = toast.loading('Analyzing image\u2026 (may take 20-60s on first run while the model loads)');
    try {
      const form = new FormData();
      form.append('image', uploadedFile);
      form.append('prompt', i2tPrompt.trim() || 'Describe this image in detail.');
      form.append('model', i2tModel);
      console.log("Making API Call to the backend API"); 
      const res = await axios.post(`${API}/vision/image-to-text`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 200_000, // 200s — model cold-start on HF can take up to 60s
      });
      setCaption(res.data.caption || '');
      toast.success('Analysis complete!', { id: toastId });
    } catch (err) {
      const detail = err.response?.data?.detail || (err.code === 'ECONNABORTED' ? 'Request timed out. The model may be cold-starting — please retry in 30 seconds.' : err.message);
      toast.error(`Analysis failed: ${detail}`, { id: toastId });
    } finally {
      setCaptioning(false);
    }
  }, [uploadedFile, token, i2tModel, i2tPrompt]);

  // ── File upload helpers ───────────────────────────────────────────────────
  const handleFileSelect = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please select a valid image file (JPEG, PNG, WEBP, GIF)');
      return;
    }
    setUploadedFile(file);
    setCaption('');
    const reader = new FileReader();
    reader.onload = e => setUploadedPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => e.preventDefault();

  // ── Styles ────────────────────────────────────────────────────────────────
  const surface = isDark ? '#0f1729' : '#ffffff';
  const surfaceSoft = isDark ? '#0a0f1e' : '#fafafa';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
  const textPrimary = isDark ? '#f0f6fc' : '#0f172a';
  const textSecondary = isDark ? '#8b949e' : '#475569';
  const textMuted = isDark ? '#6e7681' : '#94a3b8';

  return (
    <div style={{ minHeight: '100vh', background: surfaceSoft, color: textPrimary }}>
      <AppHeader />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          background: isDark
            ? 'linear-gradient(135deg, #0a0f1e 0%, #0f1729 50%, #1a0a00 100%)'
            : 'linear-gradient(135deg, #fff7f0 0%, #fff 50%, #fff7f0 100%)',
          borderBottom: `1px solid ${border}`,
          padding: '40px 0 32px',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          {/* Back button */}
          <button
            onClick={() => navigate('/marketplace')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 24,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: textMuted,
              fontSize: 14,
              padding: 0,
            }}
          >
            <ArrowLeft size={16} /> Back to Marketplace
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
            {/* Agent icon */}
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #F87217, #ff6b00)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(248,114,23,0.4)',
                flexShrink: 0,
              }}
            >
              <Eye size={28} color="#fff" />
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, fontFamily: 'Outfit, sans-serif' }}>
                  Vision AI Agent
                </h1>
                <span
                  style={{
                    padding: '3px 12px',
                    borderRadius: 20,
                    background: 'linear-gradient(135deg, #F87217, #ff6b00)',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                  }}
                >
                  NEW
                </span>
              </div>
              <p style={{ margin: '4px 0 0', color: textSecondary, fontSize: 15 }}>
                Generate stunning images from text · Caption & understand images with AI
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px 80px' }}>
        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
          <ModeTab
            active={mode === 'text-to-image'}
            onClick={() => setMode('text-to-image')}
            icon={Wand2}
            label="Text → Image"
            isDark={isDark}
          />
          <ModeTab
            active={mode === 'image-to-text'}
            onClick={() => setMode('image-to-text')}
            icon={FileText}
            label="Image → Text"
            isDark={isDark}
          />
        </div>

        {/* ── TEXT TO IMAGE ─────────────────────────────────────────────── */}
        {mode === 'text-to-image' && (
          <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 24, alignItems: 'start' }}>
            {/* Left panel — controls */}
            <div
              style={{
                borderRadius: 20,
                border: `1px solid ${border}`,
                background: surface,
                padding: 24,
                boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.06)',
                position: 'sticky',
                top: 80,
              }}
            >
              <h2 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700 }}>
                Generate Image
              </h2>

              {/* Model selector */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 8 }}>
                  AI Model
                </label>
                <ModelSelector selected={selectedModel} onChange={setSelectedModel} isDark={isDark} />
              </div>

              {/* Prompt */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 8 }}>
                  Prompt <span style={{ color: '#F87217' }}>*</span>
                </label>
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="Describe the image you want to generate…"
                  rows={5}
                  maxLength={1000}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: `1px solid ${border}`,
                    background: isDark ? '#162040' : '#f8fafc',
                    color: textPrimary,
                    fontSize: 14,
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'Inter, sans-serif',
                    lineHeight: 1.5,
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => e.target.style.borderColor = '#F87217'}
                  onBlur={e => e.target.style.borderColor = border}
                />
                <div style={{ textAlign: 'right', fontSize: 11, color: textMuted, marginTop: 4 }}>
                  {prompt.length}/1000
                </div>
              </div>

              {/* Negative prompt */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 8 }}>
                  Negative Prompt <span style={{ color: textMuted, fontWeight: 400 }}>(optional)</span>
                </label>
                <textarea
                  value={negativePrompt}
                  onChange={e => setNegativePrompt(e.target.value)}
                  placeholder="What to avoid — e.g. blurry, low quality, watermark…"
                  rows={2}
                  maxLength={500}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: `1px solid ${border}`,
                    background: isDark ? '#162040' : '#f8fafc',
                    color: textPrimary,
                    fontSize: 13,
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'Inter, sans-serif',
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => e.target.style.borderColor = '#F87217'}
                  onBlur={e => e.target.style.borderColor = border}
                />
              </div>

              {/* Generate button */}
              <button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                style={{
                  width: '100%',
                  padding: '13px',
                  borderRadius: 12,
                  border: 'none',
                  background: generating || !prompt.trim()
                    ? (isDark ? '#1e2d3d' : '#e2e8f0')
                    : 'linear-gradient(135deg, #F87217, #ff6b00)',
                  color: generating || !prompt.trim() ? textMuted : '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: generating || !prompt.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: generating || !prompt.trim() ? 'none' : '0 4px 20px rgba(248,114,23,0.4)',
                  transition: 'all 0.2s',
                }}
              >
                {generating ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</> : <><Sparkles size={18} /> Generate Image</>}
              </button>

              {/* Sample prompts */}
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 10 }}>
                  ✨ Try a sample prompt
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {SAMPLE_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setPrompt(p)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: `1px solid ${border}`,
                        background: 'none',
                        color: textSecondary,
                        fontSize: 12,
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s',
                        lineHeight: 1.4,
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = '#F87217';
                        e.currentTarget.style.color = '#F87217';
                        e.currentTarget.style.background = 'rgba(248,114,23,0.06)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = border;
                        e.currentTarget.style.color = textSecondary;
                        e.currentTarget.style.background = 'none';
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right panel — gallery */}
            <div>
              {generatedImages.length === 0 ? (
                /* Empty state */
                <div
                  style={{
                    borderRadius: 20,
                    border: `2px dashed ${border}`,
                    background: surface,
                    padding: '64px 32px',
                    textAlign: 'center',
                    color: textMuted,
                  }}
                >
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: 'rgba(248,114,23,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    <ImagePlus size={32} color="#F87217" />
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: textPrimary, marginBottom: 8 }}>
                    Your images will appear here
                  </div>
                  <div style={{ fontSize: 14, color: textSecondary, maxWidth: 360, margin: '0 auto' }}>
                    Enter a prompt on the left and click <strong>Generate Image</strong> to create stunning AI art.
                  </div>
                </div>
              ) : (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 16,
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                      Generated Images <span style={{ color: textMuted, fontWeight: 400 }}>({generatedImages.length})</span>
                    </h3>
                    <button
                      onClick={() => setGeneratedImages([])}
                      style={{
                        background: 'none',
                        border: `1px solid ${border}`,
                        borderRadius: 8,
                        padding: '6px 12px',
                        color: textMuted,
                        cursor: 'pointer',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <X size={13} /> Clear All
                    </button>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                      gap: 16,
                    }}
                  >
                    {generatedImages.map((item, idx) => (
                      <GeneratedImageCard
                        key={idx}
                        item={item}
                        isDark={isDark}
                        onReuse={(p) => { setPrompt(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── IMAGE TO TEXT ─────────────────────────────────────────────── */}
        {mode === 'image-to-text' && (
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <div
              style={{
                borderRadius: 20,
                border: `1px solid ${border}`,
                background: surface,
                padding: 32,
                boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.06)',
              }}
            >
              <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>
                Image Analysis & Understanding
              </h2>

              {/* Model selector */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 8 }}>AI Model</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {IMAGE_TO_TEXT_MODELS.map(m => (
                    <button
                      key={m.id}
                      onClick={() => setI2tModel(m.id)}
                      style={{
                        flex: 1, padding: '9px 12px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        border: `1.5px solid ${i2tModel === m.id ? m.color : border}`,
                        background: i2tModel === m.id ? m.color + '18' : 'transparent',
                        color: i2tModel === m.id ? m.color : textSecondary,
                        transition: 'all 0.15s',
                      }}
                    >
                      {m.label}
                      <span style={{ display: 'block', fontSize: 10, opacity: 0.8, marginTop: 2 }}>{m.badge}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* User prompt */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 8 }}>Instruction / Question</label>
                <textarea
                  value={i2tPrompt}
                  onChange={e => setI2tPrompt(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="e.g. What objects are in the image? What is the mood?"
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10, boxSizing: 'border-box',
                    border: `1px solid ${border}`, background: isDark ? '#162040' : '#f8fafc',
                    color: textPrimary, fontSize: 13, resize: 'vertical', outline: 'none',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  onFocus={e => e.target.style.borderColor = '#F87217'}
                  onBlur={e => e.target.style.borderColor = border}
                />
              </div>

              {/* Drop zone */}
              <div
                ref={dropZoneRef}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => !uploadedPreview && fileInputRef.current?.click()}
                style={{
                  borderRadius: 16,
                  border: `2px dashed ${uploadedPreview ? '#F87217' : border}`,
                  background: isDark ? '#0a0f1e' : '#f8fafc',
                  minHeight: uploadedPreview ? 'auto' : 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: uploadedPreview ? 'default' : 'pointer',
                  transition: 'border-color 0.2s',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {uploadedPreview ? (
                  <div style={{ width: '100%', position: 'relative' }}>
                    <img
                      src={uploadedPreview}
                      alt="Uploaded"
                      style={{
                        width: '100%',
                        maxHeight: 420,
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                    {/* Remove button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadedFile(null);
                        setUploadedPreview(null);
                        setCaption('');
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        background: 'rgba(0,0,0,0.6)',
                        border: 'none',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#fff',
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px 24px' }}>
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        background: 'rgba(248,114,23,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 14px',
                      }}
                    >
                      <Upload size={26} color="#F87217" />
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 16, color: textPrimary, marginBottom: 6 }}>
                      Drop an image here or click to upload
                    </div>
                    <div style={{ fontSize: 13, color: textMuted }}>
                      JPEG, PNG, WEBP, GIF · Max 10 MB
                    </div>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={e => handleFileSelect(e.target.files?.[0])}
              />

              {/* Actions */}
              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: 10,
                    border: `1px solid ${border}`,
                    background: 'none',
                    color: textSecondary,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <ImagePlus size={16} /> {uploadedPreview ? 'Replace Image' : 'Choose File'}
                </button>
                <button
                  onClick={handleCaption}
                  disabled={captioning || !uploadedFile}
                  style={{
                    flex: 2,
                    padding: '11px',
                    borderRadius: 10,
                    border: 'none',
                    background: captioning || !uploadedFile
                      ? (isDark ? '#1e2d3d' : '#e2e8f0')
                      : 'linear-gradient(135deg, #F87217, #ff6b00)',
                    color: captioning || !uploadedFile ? textMuted : '#fff',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: captioning || !uploadedFile ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: captioning || !uploadedFile ? 'none' : '0 4px 16px rgba(248,114,23,0.35)',
                  }}
                >
                  {captioning
                    ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Analyzing…</>
                    : <><Eye size={16} /> Generate Caption</>
                  }
                </button>
              </div>

              {/* Caption output */}
              {caption && (
                <div
                  style={{
                    marginTop: 24,
                    padding: 20,
                    borderRadius: 14,
                    background: isDark ? '#0a0f1e' : '#f0f9ff',
                    border: `1px solid ${isDark ? 'rgba(100,160,255,0.15)' : 'rgba(59,130,246,0.15)'}`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 14, color: textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Eye size={15} color="#F87217" /> AI Caption
                    </div>
                    <button
                      onClick={() => copyToClipboard(caption)}
                      style={{
                        background: 'none',
                        border: `1px solid ${border}`,
                        borderRadius: 6,
                        padding: '4px 10px',
                        cursor: 'pointer',
                        color: textMuted,
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                  <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: textPrimary }}>
                    {caption}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Spin keyframe */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}